'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface TeamInputs {
  productComplexity: number; // 1-5
  technicalComplexity: number; // 1-5
  currentTeamSize: number;
  monthlyActiveUsers: number;
  growthStage: 'pre-product' | 'early' | 'growth' | 'scale' | 'mature';
  releaseFrequency: 'weekly' | 'biweekly' | 'monthly' | 'quarterly';
  platformCount: number; // web, mobile, API, etc.
  averageSalary: number; // for budget calculation
}

interface EditableBreakdown {
  frontend: number;
  backend: number;
  fullstack: number;
  mobile: number;
  devops: number;
  qa: number;
  engineering_managers: number;
}

interface TeamRecommendation {
  recommendedSize: number;
  breakdown: {
    frontend: number;
    backend: number;
    fullstack: number;
    mobile: number;
    devops: number;
    qa: number;
    engineering_managers: number;
  };
  hiringPlan: {
    immediate: string[];
    next3Months: string[];
    next6Months: string[];
  };
  orgStructure: string;
  estimatedBudget: {
    annual: number;
    monthly: number;
  };
  rationale: string[];
}

export default function TeamScalingCalculatorPage() {
  const [inputs, setInputs] = useState<TeamInputs>({
    productComplexity: 3,
    technicalComplexity: 3,
    currentTeamSize: 5,
    monthlyActiveUsers: 10000,
    growthStage: 'early',
    releaseFrequency: 'biweekly',
    platformCount: 2,
    averageSalary: 120000,
  });

  const [customBreakdown, setCustomBreakdown] = useState<EditableBreakdown | null>(null);
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'team-scaling-calculator',
    toolCategory: 'calculator',
  });
  const [customHiringPlan, setCustomHiringPlan] = useState<{
    immediate: string[];
    next3Months: string[];
    next6Months: string[];
  } | null>(null);
  const [isEditingBreakdown, setIsEditingBreakdown] = useState(false);
  const [isEditingHiringPlan, setIsEditingHiringPlan] = useState(false);

  const updateInput = <K extends keyof TeamInputs>(key: K, value: TeamInputs[K]) => {
    setInputs(prev => ({ ...prev, [key]: value }));
    // Reset customizations when inputs change
    setCustomBreakdown(null);
    setCustomHiringPlan(null);
  };

  const recommendation = useMemo((): TeamRecommendation => {
    const {
      productComplexity,
      technicalComplexity,
      currentTeamSize,
      monthlyActiveUsers,
      growthStage,
      releaseFrequency,
      platformCount,
      averageSalary
    } = inputs;

    // Base calculation
    const complexityScore = (productComplexity + technicalComplexity) / 2;

    // Stage multipliers
    const stageMultipliers = {
      'pre-product': 0.6,
      'early': 1.0,
      'growth': 1.5,
      'scale': 2.0,
      'mature': 2.5,
    };

    // Platform impact
    const platformMultiplier = 1 + (platformCount - 1) * 0.3;

    // Release frequency impact
    const releaseMultipliers = {
      'weekly': 1.2,
      'biweekly': 1.0,
      'monthly': 0.9,
      'quarterly': 0.8,
    };

    // User scale impact
    const userScaleMultiplier = monthlyActiveUsers < 10000 ? 0.8 :
                                monthlyActiveUsers < 100000 ? 1.0 :
                                monthlyActiveUsers < 1000000 ? 1.3 :
                                monthlyActiveUsers < 10000000 ? 1.6 : 2.0;

    // Calculate recommended size
    const baseSize = 5 + (complexityScore - 3) * 2;
    const recommendedSize = Math.round(
      baseSize *
      stageMultipliers[growthStage] *
      platformMultiplier *
      releaseMultipliers[releaseFrequency] *
      userScaleMultiplier
    );

    // Team breakdown
    const totalEngineers = Math.max(3, recommendedSize);
    const needsManager = totalEngineers >= 6;
    const managerCount = totalEngineers >= 15 ? 2 : (needsManager ? 1 : 0);
    const engineersCount = totalEngineers - managerCount;

    // Distribution based on complexity and platforms
    const frontendRatio = platformCount >= 2 ? 0.3 : 0.25;
    const backendRatio = technicalComplexity >= 4 ? 0.35 : 0.30;
    const mobileRatio = platformCount >= 3 ? 0.15 : 0;
    const devopsRatio = growthStage === 'scale' || growthStage === 'mature' ? 0.15 : 0.10;
    const qaRatio = releaseFrequency === 'weekly' ? 0.15 : 0.10;
    const fullstackRatio = Math.max(0, 1 - frontendRatio - backendRatio - mobileRatio - devopsRatio - qaRatio);

    const breakdown = {
      frontend: Math.round(engineersCount * frontendRatio),
      backend: Math.round(engineersCount * backendRatio),
      fullstack: Math.round(engineersCount * fullstackRatio),
      mobile: Math.round(engineersCount * mobileRatio),
      devops: Math.max(1, Math.round(engineersCount * devopsRatio)),
      qa: Math.round(engineersCount * qaRatio),
      engineering_managers: managerCount,
    };

    // Hiring plan
    const gap = recommendedSize - currentTeamSize;
    const hiringPlan = {
      immediate: [] as string[],
      next3Months: [] as string[],
      next6Months: [] as string[],
    };

    if (gap > 0) {
      // Prioritize critical roles
      if (breakdown.devops > 0 && currentTeamSize < 5) {
        hiringPlan.immediate.push('DevOps Engineer (critical for scaling)');
      }
      if (breakdown.backend > currentTeamSize * 0.3) {
        hiringPlan.immediate.push('Senior Backend Engineer');
      }
      if (breakdown.frontend > currentTeamSize * 0.2) {
        hiringPlan.next3Months.push('Frontend Engineer');
      }
      if (needsManager && managerCount > 0) {
        hiringPlan.next3Months.push('Engineering Manager');
      }
      if (breakdown.mobile > 0) {
        hiringPlan.next3Months.push('Mobile Engineer');
      }
      if (breakdown.qa > 0 && releaseFrequency === 'weekly') {
        hiringPlan.next3Months.push('QA Engineer');
      }

      const remaining = Math.max(0, gap - hiringPlan.immediate.length - hiringPlan.next3Months.length);
      for (let i = 0; i < remaining; i++) {
        hiringPlan.next6Months.push('Additional Engineer');
      }
    }

    // Org structure
    let orgStructure = '';
    if (totalEngineers < 6) {
      orgStructure = 'Flat structure: All engineers report to CTO';
    } else if (totalEngineers < 15) {
      orgStructure = 'Single team structure: 1 Engineering Manager + engineers';
    } else if (totalEngineers < 30) {
      orgStructure = 'Multi-team structure: 2-3 teams, each with Engineering Manager';
    } else {
      orgStructure = 'Department structure: VP Engineering + multiple Engineering Managers + teams';
    }

    // Budget
    const estimatedBudget = {
      annual: recommendedSize * averageSalary * 1.3, // 30% overhead for benefits, equipment, etc.
      monthly: (recommendedSize * averageSalary * 1.3) / 12,
    };

    // Rationale
    const rationale = [
      `Complexity score of ${complexityScore.toFixed(1)}/5 suggests ${complexityScore >= 4 ? 'significant' : complexityScore >= 3 ? 'moderate' : 'basic'} technical challenges`,
      `${growthStage.charAt(0).toUpperCase() + growthStage.slice(1)} stage requires ${stageMultipliers[growthStage]}x baseline capacity`,
      `Supporting ${platformCount} platform${platformCount > 1 ? 's' : ''} increases coordination needs by ${((platformMultiplier - 1) * 100).toFixed(0)}%`,
      `${releaseFrequency.charAt(0).toUpperCase() + releaseFrequency.slice(1)} releases require ${releaseMultipliers[releaseFrequency]}x process maturity`,
      `${monthlyActiveUsers.toLocaleString()} MAU demands ${userScaleMultiplier}x operational capacity`,
    ];

    if (gap > 0) {
      rationale.push(`Current team of ${currentTeamSize} is ${gap} engineer${gap > 1 ? 's' : ''} below recommended capacity`);
    } else if (gap < 0) {
      rationale.push(`Current team of ${currentTeamSize} exceeds recommended size - consider process optimization`);
    }

    return {
      recommendedSize: totalEngineers,
      breakdown: customBreakdown || breakdown,
      hiringPlan: customHiringPlan || hiringPlan,
      orgStructure,
      estimatedBudget,
      rationale,
    };
  }, [inputs, customBreakdown, customHiringPlan]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const breakdownScores: Record<string, number> = {};
    Object.entries(recommendation.breakdown).forEach(([role, count]) => {
      if (count > 0) {
        breakdownScores[role.replace('_', ' ')] = count;
      }
    });

    const recommendations: Array<{
      id: string;
      title: string;
      priority: 'high' | 'medium' | 'low';
      description: string;
    }> = [
      ...recommendation.hiringPlan.immediate.map((role, idx) => ({
        id: `immediate-${idx}`,
        title: role,
        priority: 'high' as const,
        description: 'Hire immediately',
      })),
      ...recommendation.hiringPlan.next3Months.map((role, idx) => ({
        id: `3month-${idx}`,
        title: role,
        priority: 'medium' as const,
        description: 'Hire within next 3 months',
      })),
      ...recommendation.hiringPlan.next6Months.map((role, idx) => ({
        id: `6month-${idx}`,
        title: role,
        priority: 'low' as const,
        description: 'Hire within next 6 months',
      })),
    ];

    const assessment = await saveResult({
      name: `Team Scaling Analysis - ${new Date().toLocaleDateString()}`,
      score_overall: (recommendation.recommendedSize / Math.max(inputs.currentTeamSize, 1)) * 100,
      scores: breakdownScores,
      answers: {
        productComplexity: inputs.productComplexity,
        technicalComplexity: inputs.technicalComplexity,
        currentTeamSize: inputs.currentTeamSize,
        monthlyActiveUsers: inputs.monthlyActiveUsers,
        growthStage: inputs.growthStage,
        releaseFrequency: inputs.releaseFrequency,
        platformCount: inputs.platformCount,
        averageSalary: inputs.averageSalary,
      },
      recommendations,
      metadata: {
        recommendedSize: recommendation.recommendedSize,
        currentSize: inputs.currentTeamSize,
        gap: recommendation.recommendedSize - inputs.currentTeamSize,
        orgStructure: recommendation.orgStructure,
        estimatedBudget: recommendation.estimatedBudget,
        rationale: recommendation.rationale,
        teamBreakdown: recommendation.breakdown,
        hiringPlan: recommendation.hiringPlan,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">👥</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">
              Team Scaling Calculator
            </h1>
          </div>
          <p className="text-xl text-foreground-secondary">
            Determine optimal team size and structure based on your product roadmap and technical complexity
          </p>
        </div>

        {/* Breadcrumb */}
        <nav className="mb-8 text-sm text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">
            Home
          </Link>
          <span className="mx-2">/</span>
          <Link href="/tools" className="hover:text-accent transition-colors">
            Tools
          </Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Team Scaling Calculator</span>
        </nav>

        {/* Instructions */}
        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this calculator</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>Enter your product and technical complexity levels</li>
            <li>Specify your current team size and user base</li>
            <li>Select your growth stage and release frequency</li>
            <li>Review the recommended team size, structure, and hiring plan</li>
          </ol>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column: Inputs */}
          <div className="space-y-6">
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-6">Team & Product Details</h3>

              {/* Current Team Size */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Current Engineering Team Size
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="0"
                  max="500"
                  value={inputs.currentTeamSize}
                  onChange={(e) => updateInput('currentTeamSize', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                />
              </div>

              {/* Product Complexity */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Product Complexity: {inputs.productComplexity}/5
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={inputs.productComplexity}
                  onChange={(e) => updateInput('productComplexity', parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-foreground-tertiary mt-1">
                  <span>Simple</span>
                  <span>Moderate</span>
                  <span>Complex</span>
                </div>
              </div>

              {/* Technical Complexity */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Technical Complexity: {inputs.technicalComplexity}/5
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={inputs.technicalComplexity}
                  onChange={(e) => updateInput('technicalComplexity', parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-foreground-tertiary mt-1">
                  <span>Simple</span>
                  <span>Moderate</span>
                  <span>Complex</span>
                </div>
              </div>

              {/* Monthly Active Users */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Monthly Active Users
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="0"
                  value={inputs.monthlyActiveUsers}
                  onChange={(e) => updateInput('monthlyActiveUsers', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                />
              </div>

              {/* Growth Stage */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Growth Stage
                </label>
                <select
                  value={inputs.growthStage}
                  onChange={(e) => updateInput('growthStage', e.target.value as TeamInputs['growthStage'])}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                >
                  <option value="pre-product">Pre-Product (MVP)</option>
                  <option value="early">Early (Product-Market Fit)</option>
                  <option value="growth">Growth (Scaling)</option>
                  <option value="scale">Scale (Established)</option>
                  <option value="mature">Mature (Optimizing)</option>
                </select>
              </div>

              {/* Release Frequency */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Release Frequency
                </label>
                <select
                  value={inputs.releaseFrequency}
                  onChange={(e) => updateInput('releaseFrequency', e.target.value as TeamInputs['releaseFrequency'])}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>

              {/* Platform Count */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Number of Platforms
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="1"
                  max="10"
                  value={inputs.platformCount}
                  onChange={(e) => updateInput('platformCount', parseInt(e.target.value) || 1)}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                />
                <p className="text-xs text-foreground-tertiary mt-1">Web, iOS, Android, API, etc.</p>
              </div>

              {/* Average Salary */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Average Engineer Salary (for budget estimate)
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="0"
                  step="1000"
                  value={inputs.averageSalary}
                  onChange={(e) => updateInput('averageSalary', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                />
              </div>
            </div>
          </div>

          {/* Right Column: Results */}
          <div className="space-y-6">
            {/* Recommended Team Size */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Recommended Team Size</h3>
              <div className="text-center py-6">
                <div className="text-6xl font-bold text-accent mb-2">
                  {recommendation.recommendedSize}
                </div>
                <div className="text-foreground-secondary">Engineers</div>
                {inputs.currentTeamSize !== recommendation.recommendedSize && (
                  <div className="mt-4 text-sm">
                    {inputs.currentTeamSize < recommendation.recommendedSize ? (
                      <span className="text-accent">
                        +{recommendation.recommendedSize - inputs.currentTeamSize} from current team
                      </span>
                    ) : (
                      <span className="text-foreground-tertiary">
                        {inputs.currentTeamSize - recommendation.recommendedSize} over recommended
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Team Breakdown */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-foreground">Recommended Team Breakdown</h3>
                <button
                  onClick={() => {
                    if (isEditingBreakdown) {
                      setIsEditingBreakdown(false);
                    } else {
                      setCustomBreakdown(recommendation.breakdown);
                      setIsEditingBreakdown(true);
                    }
                  }}
                  className="text-sm px-3 py-1 bg-accent text-accent-foreground rounded hover:bg-accent-hover transition-colors"
                >
                  {isEditingBreakdown ? 'Done' : 'Customize'}
                </button>
              </div>
              <div className="space-y-3">
                {Object.entries(recommendation.breakdown).map(([role, count]) => (
                  count > 0 && (
                    <div key={role} className="flex items-center justify-between">
                      <span className="text-foreground-secondary capitalize">
                        {role.replace('_', ' ')}
                      </span>
                      {isEditingBreakdown && customBreakdown ? (
                        <input
                          type="number" inputMode="numeric"
                          min="0"
                          value={customBreakdown[role as keyof EditableBreakdown]}
                          onChange={(e) => setCustomBreakdown({
                            ...customBreakdown,
                            [role]: parseInt(e.target.value) || 0
                          })}
                          className="w-20 px-2 py-1 bg-background border border-border rounded text-accent text-center font-bold"
                        />
                      ) : (
                        <span className="text-accent font-bold">{count}</span>
                      )}
                    </div>
                  )
                ))}
              </div>
              {isEditingBreakdown && (
                <button
                  onClick={() => {
                    setCustomBreakdown(null);
                    setIsEditingBreakdown(false);
                  }}
                  className="mt-4 w-full text-sm px-3 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 transition-colors"
                >
                  Reset to Recommended
                </button>
              )}
            </div>

            {/* Org Structure */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Organization Structure</h3>
              <p className="text-foreground-secondary">{recommendation.orgStructure}</p>
            </div>

            {/* Hiring Plan */}
            {(recommendation.hiringPlan.immediate.length > 0 ||
              recommendation.hiringPlan.next3Months.length > 0 ||
              recommendation.hiringPlan.next6Months.length > 0) && (
              <div className="bg-surface rounded-lg border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-foreground">Hiring Plan</h3>
                  <button
                    onClick={() => {
                      if (isEditingHiringPlan) {
                        setIsEditingHiringPlan(false);
                      } else {
                        setCustomHiringPlan(recommendation.hiringPlan);
                        setIsEditingHiringPlan(true);
                      }
                    }}
                    className="text-sm px-3 py-1 bg-accent text-accent-foreground rounded hover:bg-accent-hover transition-colors"
                  >
                    {isEditingHiringPlan ? 'Done' : 'Customize'}
                  </button>
                </div>

                {recommendation.hiringPlan.immediate.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-accent mb-2">Hire Immediately</h4>
                    {isEditingHiringPlan ? (
                      <textarea
                        value={(customHiringPlan?.immediate || []).join('\n')}
                        onChange={(e) => setCustomHiringPlan({
                          ...customHiringPlan!,
                          immediate: e.target.value.split('\n').filter(s => s.trim())
                        })}
                        rows={3}
                        placeholder="Enter roles, one per line"
                        className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                      />
                    ) : (
                      <ul className="space-y-1">
                        {recommendation.hiringPlan.immediate.map((role, i) => (
                          <li key={i} className="text-foreground-secondary text-sm">• {role}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {recommendation.hiringPlan.next3Months.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-accent mb-2">Next 3 Months</h4>
                    {isEditingHiringPlan ? (
                      <textarea
                        value={(customHiringPlan?.next3Months || []).join('\n')}
                        onChange={(e) => setCustomHiringPlan({
                          ...customHiringPlan!,
                          next3Months: e.target.value.split('\n').filter(s => s.trim())
                        })}
                        rows={3}
                        placeholder="Enter roles, one per line"
                        className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                      />
                    ) : (
                      <ul className="space-y-1">
                        {recommendation.hiringPlan.next3Months.map((role, i) => (
                          <li key={i} className="text-foreground-secondary text-sm">• {role}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {recommendation.hiringPlan.next6Months.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-accent mb-2">Next 6 Months</h4>
                    {isEditingHiringPlan ? (
                      <textarea
                        value={(customHiringPlan?.next6Months || []).join('\n')}
                        onChange={(e) => setCustomHiringPlan({
                          ...customHiringPlan!,
                          next6Months: e.target.value.split('\n').filter(s => s.trim())
                        })}
                        rows={3}
                        placeholder="Enter roles, one per line"
                        className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                      />
                    ) : (
                      <ul className="space-y-1">
                        {recommendation.hiringPlan.next6Months.map((role, i) => (
                          <li key={i} className="text-foreground-secondary text-sm">• {role}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {isEditingHiringPlan && (
                  <button
                    onClick={() => {
                      setCustomHiringPlan(null);
                      setIsEditingHiringPlan(false);
                    }}
                    className="mt-4 w-full text-sm px-3 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 transition-colors"
                  >
                    Reset to Recommended
                  </button>
                )}
              </div>
            )}

            {/* Budget Estimate */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Budget Estimate</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-foreground-secondary">Annual</span>
                  <span className="text-accent font-bold text-lg">
                    {formatCurrency(recommendation.estimatedBudget.annual)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-secondary">Monthly</span>
                  <span className="text-accent font-bold text-lg">
                    {formatCurrency(recommendation.estimatedBudget.monthly)}
                  </span>
                </div>
                <p className="text-xs text-foreground-tertiary mt-2">
                  Includes 30% overhead for benefits, equipment, and other costs
                </p>
              </div>
            </div>

            {/* Rationale */}
            <div className="bg-accent-muted border border-border rounded-lg p-6">
              <h3 className="text-lg font-bold text-accent mb-3">Rationale</h3>
              <ul className="space-y-2">
                {recommendation.rationale.map((reason, i) => (
                  <li key={i} className="text-foreground-secondary text-sm">• {reason}</li>
                ))}
              </ul>
            </div>

            {/* Command Center Save Button */}
            <div className="flex flex-wrap gap-4">
              {commandCenterEnabled && !saved && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              )}
              {saved && (
                <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved to Command Center
                </div>
              )}
              {saveError && (
                <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
                  {saveError}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="mt-8 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-bold text-foreground mb-3">Related Resources</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              href="/frameworks"
              className="p-4 bg-background rounded border border-border-muted hover:border-border transition-colors"
            >
              <h4 className="font-bold text-foreground mb-1">Hiring Frameworks →</h4>
              <p className="text-sm text-foreground-tertiary">
                Structured approaches to technical hiring and team building
              </p>
            </Link>
            <Link
              href="/templates"
              className="p-4 bg-background rounded border border-border-muted hover:border-border transition-colors"
            >
              <h4 className="font-bold text-foreground mb-1">Org Templates →</h4>
              <p className="text-sm text-foreground-tertiary">
                Organization charts and role definitions
              </p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
